// The GDL estate's regions: the single source of every region-dependent value
// the spoke scaffolder writes into GDL_G_D_B_HubSpoke_V2 tfvars files, and of
// the regions the request form offers.
//
// `code` is the estate's own region token (HGS-GDL-<code>-PREPROD, RG-GDL-NET-
// STG-<code>, hub_region, ...), not Azure's abbreviation: East US 2 is UE.
// Filenames use the same token in lowercase.
//
// A value that is not known is left undefined. resolveEstateRegion() refuses
// such a region by name rather than letting a guess or a placeholder reach a
// tfvars file.

export interface EstateSubscription {
  name: string;
  id: string;
}

export interface EstateRegion {
  code: string;
  /** Azure's display name for the location; also what users see. */
  location: string;
  preprodSubscription: EstateSubscription;
  prodSubscription: EstateSubscription;
  metastoreId?: string;
  hubFirewallIp?: string;
  stgVnetAddressSpace?: string;
  prodVnetAddressSpace?: string;
}

const subscriptions = (code: string, preprodId: string, prodId: string) => ({
  preprodSubscription: { name: `HGS-GDL-${code}-PREPROD`, id: preprodId },
  prodSubscription: { name: `HGS-GDL-${code}-PROD`, id: prodId },
});

// Sources, checked 2026-09-25: subscriptions from `az account list`; locations
// and VNet ranges from the live VNET-GDL-NET-{STG,PROD}-<code> networks;
// metastore ids from the platform team, confirmed against live workspaces for
// WE, UE, UKS, NE and AE (UC, SA and EA have no workspace to confirm against);
// firewall ips from the existing hub-spoke network files.
export const ESTATE_REGIONS: readonly EstateRegion[] = [
  {
    code: 'WE',
    location: 'West Europe',
    ...subscriptions(
      'WE',
      '69d2f557-9b97-48eb-b965-0a4039439c9d',
      '2909ae58-7dba-43d0-9b47-961fb62a1885',
    ),
    metastoreId: '4d112fbb-d921-4c06-8644-c18c6cf40e10',
    hubFirewallIp: '10.11.241.10',
    stgVnetAddressSpace: '10.19.128.0/18',
    prodVnetAddressSpace: '10.19.192.0/18',
  },
  {
    code: 'UE',
    location: 'East US 2',
    ...subscriptions(
      'UE',
      '76a70d91-0124-4b2d-ab30-288e5538e910',
      'a79fa6f9-c0e2-48cd-958a-79da004c6558',
    ),
    metastoreId: 'dde5a000-fcab-400b-b6d8-bd04c30855b5',
    hubFirewallIp: '10.85.14.75',
    stgVnetAddressSpace: '10.89.128.0/18',
    prodVnetAddressSpace: '10.89.192.0/18',
  },
  {
    code: 'AE',
    location: 'Australia East',
    ...subscriptions(
      'AE',
      'f82a17ac-4bf4-41ee-91c7-ada85de8b1b0',
      '92f5121d-6252-4ea9-896c-525877b24bd6',
    ),
    metastoreId: '93d3d049-9beb-428c-b15a-5f99e98f9112',
    // Not known: no AE hub-spoke network file exists yet to take it from.
    hubFirewallIp: undefined,
    stgVnetAddressSpace: '10.80.128.0/18',
    prodVnetAddressSpace: '10.80.192.0/18',
  },
  {
    code: 'UKS',
    location: 'UK South',
    ...subscriptions(
      'UKS',
      '7e8d408b-420a-45c5-b1c2-0a23a8444fdd',
      '3bf09cde-460c-4ee5-a8b3-5eaf806b4dce',
    ),
    metastoreId: 'f200e235-76ef-4b72-b95c-e2650b0a5c63',
    hubFirewallIp: '10.12.241.10',
    stgVnetAddressSpace: '10.20.128.0/18',
    prodVnetAddressSpace: '10.20.192.0/18',
  },
  {
    code: 'UC',
    location: 'Central US',
    ...subscriptions(
      'UC',
      'cc3ab8db-f3f9-497f-819e-4528241a49f9',
      '67fdae68-43dc-4974-9fa8-8c09bc838f67',
    ),
    metastoreId: '3a66fbf1-3924-4425-80f6-cd35de1e0e11',
    hubFirewallIp: '10.84.241.10',
    stgVnetAddressSpace: '10.88.128.0/18',
    prodVnetAddressSpace: '10.88.192.0/18',
  },
  {
    code: 'SA',
    location: 'Southeast Asia',
    ...subscriptions(
      'SA',
      'eccc80ca-6c34-4a91-ac48-6e5631e30725',
      'beb2bcc2-27c2-4ccb-b036-8778bb54d949',
    ),
    metastoreId: 'c795de36-9d4d-4df3-939a-12fa77165e00',
    hubFirewallIp: '10.64.241.10',
    stgVnetAddressSpace: '10.78.128.0/18',
    prodVnetAddressSpace: '10.78.192.0/18',
  },
  {
    code: 'EA',
    location: 'East Asia',
    ...subscriptions(
      'EA',
      '8d662cf7-a013-4a8d-9980-f30e8e9799ec',
      '00d14895-3d08-4b38-97c8-c1fad470ef5a',
    ),
    metastoreId: '5ced449f-e6d5-4f42-bfc8-156d3f650994',
    hubFirewallIp: '10.65.241.10',
    stgVnetAddressSpace: '10.79.128.0/18',
    prodVnetAddressSpace: '10.79.192.0/18',
  },
  {
    code: 'NE',
    location: 'North Europe',
    ...subscriptions(
      'NE',
      '3c8427fb-accd-458b-8901-6daee8d88c65',
      '961e88c4-60ab-42ef-a0e8-706777fec73e',
    ),
    metastoreId: 'e99f5c39-13f1-4513-a957-276bc1424dec',
    hubFirewallIp: '10.6.241.10',
    stgVnetAddressSpace: '10.18.128.0/18',
    prodVnetAddressSpace: '10.18.192.0/18',
  },
];

/** Everything the spoke templates need for one region, with nothing missing. */
export interface ResolvedEstateRegion {
  code: string;
  /** The token in lowercase: filenames, storage accounts, containers. */
  lowerCode: string;
  location: string;
  preprodSubscription: EstateSubscription;
  prodSubscription: EstateSubscription;
  metastoreId: string;
  hubFirewallIp: string;
  stgVnetAddressSpace: string;
  prodVnetAddressSpace: string;
}

const REQUIRED_VALUES = [
  ['metastoreId', 'Databricks metastore id'],
  ['hubFirewallIp', 'hub firewall IP'],
  ['stgVnetAddressSpace', 'STG VNet address space'],
  ['prodVnetAddressSpace', 'PROD VNet address space'],
] as const;

export function findEstateRegion(code: string): EstateRegion | undefined {
  return ESTATE_REGIONS.find(region => region.code === code);
}

/** Names of the values still missing for a region; empty when complete. */
export function missingEstateRegionValues(region: EstateRegion): string[] {
  return REQUIRED_VALUES.filter(([key]) => !region[key]).map(
    ([, label]) => label,
  );
}

export function resolveEstateRegion(code: string): ResolvedEstateRegion {
  const region = findEstateRegion(code);
  if (!region) {
    throw new Error(
      `Region '${code}' is not an estate region. Expected one of: ${ESTATE_REGIONS.map(
        r => r.code,
      ).join(', ')}.`,
    );
  }
  const missing = missingEstateRegionValues(region);
  if (missing.length > 0) {
    throw new Error(
      `Spokes cannot be generated for ${region.location} (${
        region.code
      }) yet: the ${missing.join(', ')} ${
        missing.length === 1 ? 'is' : 'are'
      } not known. Ask the platform team to add ${
        missing.length === 1 ? 'it' : 'them'
      } to the region table.`,
    );
  }
  return {
    code: region.code,
    lowerCode: region.code.toLowerCase(),
    location: region.location,
    preprodSubscription: region.preprodSubscription,
    prodSubscription: region.prodSubscription,
    metastoreId: region.metastoreId!,
    hubFirewallIp: region.hubFirewallIp!,
    stgVnetAddressSpace: region.stgVnetAddressSpace!,
    prodVnetAddressSpace: region.prodVnetAddressSpace!,
  };
}

// The hub-spoke pipeline names the PROD vaults KV-GDL-<DIV>-PRD-<REGION> and
// KV-<DIV>-WL-PROD-<REGION>; Azure caps vault names at 24 characters.
const KEY_VAULT_NAME_LIMIT = 24;
const KEY_VAULT_FIXED_CHARS = 'KV-GDL--PRD-'.length;

export function maxSpokeNameLength(code: string): number {
  return KEY_VAULT_NAME_LIMIT - KEY_VAULT_FIXED_CHARS - code.length;
}

/** Why a spoke name is not usable in a region, or undefined when it is. */
export function spokeNameProblem(
  name: string,
  code: string,
): string | undefined {
  if (!/^[a-z0-9]+$/.test(name)) {
    return 'Use only lowercase letters (a-z) and digits (0-9).';
  }
  const max = maxSpokeNameLength(code);
  if (name.length > max) {
    return `Use at most ${max} characters in ${
      findEstateRegion(code)?.location ?? code
    }; the production key vault names would exceed Azure's 24-character limit.`;
  }
  return undefined;
}
